/**
 * DriverDeliveryView — Mobile-optimized delivery driver view.
 *
 * Shows current delivery, navigation, and status updates.
 * Follows DRR-055: Dumb Frontend — all data from backend.
 */

import { useMemo, useCallback, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Loader,
  Divider,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconTruck,
  IconMapPin,
  IconPhone,
  IconCheck,
  IconNavigation,
  IconRefresh,
  IconPackage,
  IconArrowRight,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
import {
  useFoodServiceDriverDeliveriesQuery,
  useFoodServiceDriverDailyStatsQuery,
  FoodServiceDriverStatus,
} from '@/generated/graphql';

export function DriverDeliveryView() {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;

  const { actorId } = useMyActor();
  const [currentDeliveryId, setCurrentDeliveryId] = useState<string | null>(null);

  // Query deliveries for this driver using Food Service API
  const {
    data: deliveriesData,
    loading: deliveriesLoading,
    refetch: refetchDeliveries,
  } = useFoodServiceDriverDeliveriesQuery({
    variables: { driverId: actorId ?? '' },
    skip: skip || !actorId,
    pollInterval: 10000,
  });

  // Query daily stats (DRR-055: backend-driven)
  const { data: statsData } = useFoodServiceDriverDailyStatsQuery({
    skip: skip || !actorId,
    pollInterval: 60000,
  });
  const dailyStats = statsData?.driverDailyStats;

  const deliveries = deliveriesData?.driverDeliveries ?? [];

  // Current delivery is the first one
  const currentDelivery = deliveries[0];
  const upcomingDeliveries = deliveries.slice(1);

  const handleRefresh = useCallback(() => {
    refetchDeliveries();
  }, [refetchDeliveries]);

  const handleNavigate = useCallback((address: string) => {
    // Open in maps app
    const encoded = encodeURIComponent(address);
    window.open(`https://maps.google.com/?q=${encoded}`, '_blank');
  }, []);

  const handleArrived = useCallback((deliveryId: string) => {
    // TODO: Implement arrival mutation
    console.log('Arrived at:', deliveryId);
  }, []);

  const handleDelivered = useCallback((deliveryId: string) => {
    // TODO: Implement delivery complete mutation
    console.log('Delivered:', deliveryId);
    setCurrentDeliveryId(null);
  }, []);

  const handlePickup = useCallback((deliveryId: string) => {
    // TODO: Implement pickup mutation
    console.log('Picked up:', deliveryId);
    setCurrentDeliveryId(deliveryId);
  }, []);

  if (deliveriesLoading) {
    return (
      <Container size="sm" py="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Loading deliveries...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="sm" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" color="teal" variant="light">
              <IconTruck size={28} />
            </ThemeIcon>
            <div>
              <Title order={2}>My Deliveries</Title>
              <Text size="sm" c="dimmed">
                {deliveries.length} deliveries
              </Text>
            </div>
          </Group>
          <Tooltip label="Refresh">
            <ActionIcon variant="light" size="lg" onClick={handleRefresh}>
              <IconRefresh size={20} />
            </ActionIcon>
          </Tooltip>
        </Group>

        {/* Current Delivery */}
        {currentDelivery ? (
          <Paper p="md" withBorder style={{ borderColor: 'var(--mantine-color-teal-6)', borderWidth: 2 }}>
            <Badge color="teal" size="lg" mb="md">
              CURRENT DELIVERY
            </Badge>

            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={700} size="xl">
                  #{currentDelivery.orderNumber}
                </Text>
                <Badge color="blue">{currentDelivery.brand}</Badge>
              </Group>

              <Divider />

              {/* Address */}
              <Group>
                <ThemeIcon color="gray" variant="light">
                  <IconMapPin size={20} />
                </ThemeIcon>
                <div style={{ flex: 1 }}>
                  <Text size="sm" c="dimmed">
                    Delivery Address
                  </Text>
                  <Text fw={500}>{currentDelivery.deliveryAddress}</Text>
                </div>
              </Group>

              {/* Customer Phone */}
              <Group>
                <ThemeIcon color="gray" variant="light">
                  <IconPhone size={20} />
                </ThemeIcon>
                <div>
                  <Text size="sm" c="dimmed">
                    {currentDelivery.customerName}
                  </Text>
                  <Text fw={500}>{currentDelivery.customerPhone}</Text>
                </div>
              </Group>

              <Divider />

              {/* Order Items */}
              <div>
                <Text size="sm" c="dimmed" mb="xs">
                  Items
                </Text>
                <Text>{currentDelivery.items}</Text>
                {currentDelivery.specialInstructions && (
                  <Text size="sm" c="dimmed" mt="xs">
                    Notes: "{currentDelivery.specialInstructions}"
                  </Text>
                )}
              </div>

              <Divider />

              {/* Action Buttons */}
              <Stack gap="sm">
                <Button
                  fullWidth
                  size="lg"
                  color="blue"
                  leftSection={<IconNavigation size={20} />}
                  onClick={() => handleNavigate(currentDelivery.deliveryAddress)}
                >
                  Navigate
                </Button>
                <Group grow>
                  <Button
                    size="lg"
                    variant="light"
                    color="yellow"
                    onClick={() => handleArrived(currentDelivery.id)}
                  >
                    Arrived
                  </Button>
                  <Button
                    size="lg"
                    color="green"
                    leftSection={<IconCheck size={20} />}
                    onClick={() => handleDelivered(currentDelivery.id)}
                  >
                    Delivered
                  </Button>
                </Group>
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={60} color="gray" variant="light">
                <IconPackage size={36} />
              </ThemeIcon>
              <Text size="lg" c="dimmed">
                No active delivery
              </Text>
              <Text size="sm" c="dimmed">
                Check upcoming orders below
              </Text>
            </Stack>
          </Paper>
        )}

        {/* Upcoming / Queue */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Next Up
          </Title>
          {upcomingDeliveries.length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No more deliveries in queue
            </Text>
          ) : (
            <Stack gap="sm">
              {upcomingDeliveries.slice(0, 5).map((delivery) => (
                <Card key={delivery.id} withBorder padding="sm">
                  <Group justify="space-between">
                    <div>
                      <Text fw={500}>#{delivery.orderNumber} - {delivery.brand}</Text>
                      <Group gap="xs">
                        <IconMapPin size={14} />
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {delivery.deliveryAddress}
                        </Text>
                      </Group>
                    </div>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        ~{delivery.estimatedArrivalMinutes ?? '—'} min
                      </Text>
                      <IconArrowRight size={14} />
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Stats (DRR-055: backend-driven) */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Today's Stats
          </Title>
          <Group grow>
            <Stack align="center" gap="xs">
              <Text size="xl" fw={700}>
                {dailyStats?.deliveriesCompleted ?? 0}
              </Text>
              <Text size="xs" c="dimmed">
                Deliveries
              </Text>
            </Stack>
            <Stack align="center" gap="xs">
              <Text size="xl" fw={700}>
                ${dailyStats?.tipsCents ? (dailyStats.tipsCents / 100).toFixed(0) : '0'}
              </Text>
              <Text size="xs" c="dimmed">
                Tips
              </Text>
            </Stack>
            <Stack align="center" gap="xs">
              <Text size="xl" fw={700}>
                {dailyStats?.avgDeliveryMinutes?.toFixed(0) ?? '—'} min
              </Text>
              <Text size="xs" c="dimmed">
                Avg Time
              </Text>
            </Stack>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}

export default DriverDeliveryView;
